/* eslint-disable no-underscore-dangle */
import {
  DowncastConversionApi,
  Element as ModelElement,
  Plugin,
  toWidget,
  viewToModelPositionOutsideModelElement,
  Widget,
} from 'ckeditor5'
import { v4 as uuid } from 'uuid'

// There're basically 2 types of conversion, upcast and downcast.
// 1. Upcast is when you're loading data to your editor
// 2. Downcast is when you're making changes the editor.
// https://ckeditor.com/docs/ckeditor5/latest/framework/guides/deep-dive/conversion/intro.html
export default class CustomSignature extends Plugin {
  static get requires() {
    return [Widget]
  }

  init() {
    this._defineSchema()
    this._defineConverters()

    // Can import translation the following example
    // const Signature = enUSPbtCKEditor.Common.SIGNATURE as unknown as string
    // addTranslation('es', { [Signature]: esESPbtCKEditor.Common.SIGNATURE })

    // This is important to avoid the editor ui throwing errors while editing
    this.editor.editing.mapper.on(
      'viewToModelPosition',
      viewToModelPositionOutsideModelElement(this.editor.model, (viewElement) =>
        viewElement.hasClass('SIGNATURE'),
      ),
    )
  }

  _defineSchema() {
    const { schema } = this.editor.model

    schema.register('signature', {
      // Whether an item is "self-contained" and should be treated as a whole. By default it sets isLimit true
      // which means that the element cannot be split by Enter
      isObject: true,
      // Inherits "allowed in" from other items, in this case $block
      allowWhere: '$block',
      allowAttributes: ['style', 'classes'],
    })
  }

  _defineConverters() {
    const { conversion, t } = this.editor

    const elementUniqueId = `SIGNATURE_${uuid()}`

    // UPCAST - Transformations applied when receiving data
    conversion.for('upcast').elementToElement({
      model: (viewElement, { writer: modelWriter }) => {
        const signatureModel = modelWriter.createElement('signature', {
          style: viewElement?.getAttribute('style') ?? '',
          id: elementUniqueId,
        })
        return signatureModel
      },
      view: {
        name: 'table',
        classes: 'SIGNATURE',
      },
      converterPriority: 'high',
    })

    function getTableWrapperElement(
      viewWriter: DowncastConversionApi['writer'],
      modelElement: ModelElement,
    ) {
      // createContainerElement types are not updated with the recent API from createContainerElement
      // eslint-disable-next-line max-len
      // https://ckeditor.com/docs/ckeditor5/latest/updating/migration-to-33.html#improved-api-of-downcastwritercreatecontainerelement
      const tableCellLabelElement = viewWriter.createContainerElement(
        'td',
        {
          class: 'SIGNATURE_label',
          style: 'border: 1px solid #edecec; padding: 8px; font-weight: 700;',
        },
        // @ts-ignore
        [t('Signature')],
      )

      const tableCellSignatureElement = viewWriter.createContainerElement(
        'td',
        {
          class: 'SIGNATURE_signature',
          style: 'border: 1px solid #edecec; padding: 8px; width: 200px',
        },
        // @ts-ignore
        [''],
      )

      // @ts-ignore
      const tableRowElement = viewWriter.createContainerElement('tr', null, [
        tableCellLabelElement,
        tableCellSignatureElement,
      ])

      const tableWrapperElement = viewWriter.createContainerElement(
        'table',
        {
          class: 'SIGNATURE',
          style: modelElement.getAttribute('style') as string,
          // @ts-ignore
        },
        [tableRowElement],
      )

      return tableWrapperElement
    }

    // DATA DOWNCAST - html retrieved state
    // @ts-ignore
    conversion.for('dataDowncast').elementToStructure({
      model: 'signature',
      view: (
        modelElement: ModelElement,
        { writer: viewWriter }: DowncastConversionApi,
      ) => {
        const tableWrapperElement = getTableWrapperElement(
          viewWriter,
          modelElement,
        )
        return tableWrapperElement
      },
      converterPriority: 'high',
    })

    // EDITING DOWNCAST - how element behaviors during any edition
    conversion.for('editingDowncast').elementToElement({
      model: 'signature',
      view: (modelElement, { writer: viewWriter }) => {
        const tableWrapperElement = getTableWrapperElement(
          viewWriter,
          modelElement,
        )

        return toWidget(tableWrapperElement, viewWriter, {
          label: 'signature view widget',
        })
      },
      converterPriority: 'high',
    })
  }
}
